import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { useField } from 'react-final-form';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import { FlexMiddle, FormModal, Small } from '@ubeya/shared-web/components';
import arrayDirtyValues from '@ubeya/shared/utils/arrayField';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import { SortableContainer } from 'react-sortable-hoc';
import Section from './Section';

const AddSectionLink = styled(Small)`
  margin-top: 10px;
  color: ${({ theme }) => theme.colors.black};
  cursor: pointer;
  font-size: 15rem;
`;

const SectionsModal = ({ selectedSection, onClose, onConfirm }) => {
  const { t } = useContext(ConfigContext);
  const { labelOptions: defaultLabels, updateLabels, editLabel } = useLabels();

  const initialValues = useMemo(
    () => ({
      shifts:
        defaultLabels?.length > 0
          ? defaultLabels.map((label) => ({
              ...label,
              id: label.value,
              title: label.label,
              locationId: label.locationId ?? undefined,
              startTime: label.startTime ?? undefined,
              endTime: label.endTime ?? undefined
            }))
          : [{ id: -Date.now(), title: '', status: 'added', priority: 1 }]
    }),
    [defaultLabels]
  );

  const validate = useCallback(
    (values) => (values.shifts.every(({ title }) => title) ? undefined : { error: 'required' }),
    []
  );

  const handleSubmit = useCallback(
    async (values, form) => {
      const { initialValues: formInitialValues } = form.getState();
      const shifts = arrayDirtyValues(formInitialValues, values, 'shifts');

      const data = await updateLabels(shifts);
      if (shifts.deleted.filter((id) => id > 0).length > 0) {
        await Promise.all(shifts.deleted.map((id) => editLabel({ id, isDeleted: true })));
      }
      onClose();
      onConfirm?.(data);
    },
    [updateLabels, onClose, onConfirm, editLabel]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validate={validate}
      mutators={{ ...arrayMutators }}
      onClose={onClose}
      size="medium"
      title={
        <FlexMiddle style={{ flex: 1 }}>
          <strong>{defaultLabels.length ? t('editSection') : t('createSection')}</strong>
        </FlexMiddle>
      }
      confirmText={defaultLabels.length ? 'update' : 'add'}
      disabledOnPristine={false}
      keepDirtyOnReinitialize>
      <SortableSections selectedSection={selectedSection} />
    </FormModal>
  );
};

const SortableSections = (props) => {
  const { handleSortItems } = useArrayManagement('shifts');

  return <Sections {...props} useDragHandle onSortEnd={handleSortItems} />;
};

const Sections = SortableContainer(({ selectedSection }) => {
  const { t } = useContext(ConfigContext);
  const {
    input: { value }
  } = useField('shifts');

  const colorArrayIndex = value.reduce(
    (prev, { id, index }) => [...prev, id > 0 ? index : (prev[prev.length - 1] ?? -1) + 1],
    []
  );

  return (
    <div>
      <FieldArray name="shifts">
        {({ fields }) => (
          <div>
            {fields.map((name, index) => (
              <Section
                key={index}
                index={index}
                name={name}
                section={fields.value[index]}
                onDelete={() => fields.remove(index)}
                isSelected={selectedSection?.id === fields.value[index].id}
                colorIndex={colorArrayIndex[index]}
              />
            ))}

            <AddSectionLink
              type="button"
              secondary
              onClick={() => fields.push({ id: -Date.now(), priority: fields.length + 1 })}>
              + {t('newSection')}
            </AddSectionLink>
          </div>
        )}
      </FieldArray>
    </div>
  );
});

export default SectionsModal;
